import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router";
import { DndProvider, useDrag, useDrop } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, TriangleAlert } from "lucide-react";
import {
  activationHint,
  monthGrid,
  monthLabel,
  shiftMonth,
  WEEKDAY_LABELS,
  type CalendarDay,
  type IsoDate,
} from "@shared-core/domain/campaign-calendar";
import { formatDateOrDash } from "@shared-core/domain/formatters";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import type { CalendarEntry } from "@shared-core/data/marketing-types";
import { paths } from "@shared-core/routing/routes";
import { calendarMailingState } from "@shared-core/domain/direct-mail-campaigns";
import { ChannelIcon } from "../app/components/domain/marketing/CampaignStepDisplay";
import { MarketingNav } from "../app/components/domain/marketing/MarketingNav";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../app/components/primitives/alert-dialog";
import { Button } from "../app/components/primitives/button";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "../app/components/primitives/sheet";
import { currentTime } from "@shared-core/domain/current-time";

const STATUS_LABEL: Record<CalendarEntry["status"], string> = {
  sent: "Sent",
  scheduled: "Scheduled",
  pending: "Pending",
  needs_attention: "Needs attention",
};

function today(): IsoDate {
  const d = currentTime();
  d.setUTCHours(0, 0, 0, 0);
  return d.toISOString().slice(0, 10);
}

const MAX_CHIPS_PER_DAY = 3;

// react-dnd drag type for a calendar entry chip. A single string constant
// (not per-entry) — every entry chip and every day cell share one drag
// channel, matching the "drag any draggable chip to any day" interaction.
const ENTRY_DRAG_TYPE = "calendar-entry";

interface EntryDragItem {
  id: string;
}

function entryChipClasses(entry: CalendarEntry): string {
  switch (entry.status) {
    case "sent":
      return "bg-muted text-muted-foreground";
    case "needs_attention":
      return "bg-[color:var(--color-error-bg,theme(colors.red.50))] text-[color:var(--color-error,theme(colors.red.700))] dark:bg-red-900/20 dark:text-red-400 font-medium";
    case "pending":
      // Not yet confirmed and no ticket blocking it — a fainter, dashed
      // treatment distinct from "scheduled" so a busy run of pencilled-in
      // sends is visible at a glance (useful for spacing decisions) without
      // reading as a real commitment the way "scheduled" does.
      return "bg-primary/5 text-primary/70 border border-dashed border-primary/30";
    case "scheduled":
    default:
      return "bg-primary/10 text-primary";
  }
}

/** One send's chip, draggable to a new day when its own can_reschedule says
 *  a drop would actually succeed (a draft tranche — never a reminder, never
 *  once sent). useDrag's canDrag makes an ineligible chip simply not
 *  draggable at the DOM level, so it keeps behaving exactly as a plain
 *  click target. */
function EntryChip({
  entry,
  onOpen,
}: {
  entry: CalendarEntry;
  onOpen: (entry: CalendarEntry) => void;
}) {
  const [{ isDragging }, dragRef] = useDrag<
    EntryDragItem,
    unknown,
    { isDragging: boolean }
  >(
    () => ({
      type: ENTRY_DRAG_TYPE,
      item: { id: entry.id },
      canDrag: entry.can_reschedule,
      collect: (monitor) => ({ isDragging: monitor.isDragging() }),
    }),
    [entry.id, entry.can_reschedule],
  );

  return (
    <button
      ref={(node) => {
        dragRef(node);
      }}
      type="button"
      onClick={() => onOpen(entry)}
      className={`w-full flex items-center gap-1 rounded px-1.5 py-0.5 text-left text-[11px] leading-tight truncate hover:opacity-80 transition-opacity ${entryChipClasses(entry)} ${
        entry.can_reschedule ? "cursor-grab active:cursor-grabbing" : ""
      } ${isDragging ? "opacity-40" : ""}`}
      title={`${mailingStatePrefix(entry)}${entry.send_label}${entry.is_date_confirmed ? "" : " (estimated)"}${entry.can_reschedule ? " — drag to a different day to change its send date" : ""}`}
    >
      {entry.status === "needs_attention" && (
        <TriangleAlert className="size-3 shrink-0" />
      )}
      {/* A mailing or call round shows its channel; email sends keep their
          plain chip. */}
      {entry.channel !== "email" && (
        <ChannelIcon channel={entry.channel} className="size-3 shrink-0" />
      )}
      {/* entry.send_label already names its own campaign (e.g. "Advent
          Calendar Aug 2026 — Tranche 1") — see list_calendar_entries'
          _calendar_entry_label_and_date — so entry.campaign_name is
          deliberately not repeated here or in the Sheet below. */}
      <span className="truncate">
        {mailingStatePrefix(entry)}
        {entry.send_label}
      </span>
    </button>
  );
}

/** A mailing says whether it is still only planned or has been posted:
 *  "Planned: " / "Posted: ". Empty for anything else. */
function mailingStatePrefix(entry: CalendarEntry): string {
  return entry.channel === "direct_mail"
    ? `${calendarMailingState(entry.state)}: `
    : "";
}

/** One day cell — a drop target for any draggable entry chip. Accepting the
 *  drop and figuring out what it means (which send, whether the date
 *  actually changed, saving it) is the page's job; this only reports which
 *  day something was dropped on. */
function DayCell({
  day,
  entries,
  loading,
  onOpenEntry,
  onDropEntry,
}: {
  day: CalendarDay;
  entries: CalendarEntry[];
  loading: boolean;
  onOpenEntry: (entry: CalendarEntry) => void;
  onDropEntry: (entryId: string, date: IsoDate) => void;
}) {
  const [{ isOver, canDrop }, dropRef] = useDrop<
    EntryDragItem,
    unknown,
    { isOver: boolean; canDrop: boolean }
  >(
    () => ({
      accept: ENTRY_DRAG_TYPE,
      drop: (item) => onDropEntry(item.id, day.date),
      collect: (monitor) => ({
        isOver: monitor.isOver(),
        canDrop: monitor.canDrop(),
      }),
    }),
    [day.date, onDropEntry],
  );

  const shown = entries.slice(0, MAX_CHIPS_PER_DAY);
  const overflow = entries.length - shown.length;

  return (
    <div
      ref={(node) => {
        dropRef(node);
      }}
      data-date={day.date}
      className={`min-h-28 border-b border-r border-border p-1.5 last:border-r-0 ${
        day.outsideMonth ? "bg-surface/50" : ""
      } ${isOver && canDrop ? "bg-primary/10 ring-1 ring-inset ring-primary" : ""}`}
    >
      <div
        className={`text-xs mb-1 inline-flex items-center justify-center size-5 rounded-full ${
          day.isToday
            ? "bg-primary text-primary-foreground font-semibold"
            : day.outsideMonth
              ? "text-muted-foreground/50"
              : "text-muted-foreground"
        }`}
      >
        {Number(day.date.slice(8, 10))}
      </div>
      <div className="space-y-1">
        {loading && entries.length === 0 && !day.outsideMonth && (
          <div className="h-4 rounded bg-surface animate-pulse" />
        )}
        {shown.map((entry) => (
          <EntryChip key={entry.id} entry={entry} onOpen={onOpenEntry} />
        ))}
        {overflow > 0 && (
          <div className="text-[11px] text-muted-foreground px-1.5">
            +{overflow} more
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * The campaign scheduling calendar
 * (docs/features/campaign-scheduling/): every send (tranche or reminder)
 * shown by its real or best-estimated send date, past and future together,
 * so this replaces the marketer's own external send-date list. Every entry
 * the backend returns always carries a date (see list_calendar_entries) —
 * is_date_confirmed distinguishes a real Mailchimp date from an estimate,
 * so a pending reminder still places itself on the grid rather than sitting
 * in a separate unscheduled list.
 */
export function CalendarPage() {
  const navigate = useNavigate();
  const dataAdapter = useDataAdapter();
  const now = today();
  const [year, setYear] = useState(() => Number(now.slice(0, 4)));
  const [month, setMonth] = useState(() => Number(now.slice(5, 7)));
  const [selectedEntry, setSelectedEntry] = useState<CalendarEntry | null>(
    null,
  );
  // A drop proposes a reschedule; it isn't saved until the confirm dialog
  // below is accepted. A plain click-and-drag (no deliberate intent to
  // move anything) is easy to trigger by accident on a chip that's also a
  // click target — this is the backstop against silently moving a send a
  // marketer didn't mean to touch.
  const [pendingDrop, setPendingDrop] = useState<{
    entry: CalendarEntry;
    newDate: IsoDate;
  } | null>(null);
  const [entries, setEntries] = useState<CalendarEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  // Bumped after a successful drag-to-reschedule to re-run the fetch below
  // rather than hand-patching local state — a moved entry's own status
  // (e.g. needs_attention → scheduled) can change as a side effect of the
  // date change, and re-deriving that client-side would duplicate the
  // backend's own status rules.
  const [refreshKey, setRefreshKey] = useState(0);

  const days = useMemo(() => monthGrid(year, month, now), [year, month, now]);

  // A day cell can show an entry from outside the displayed month (the
  // grid's leading/trailing days), so fetch a window a little wider than
  // the visible month rather than re-deriving day.date bounds by hand.
  const rangeStart = days[0]?.date;
  const rangeEnd = days[days.length - 1]?.date;

  useEffect(() => {
    if (!rangeStart || !rangeEnd) return;
    let cancelled = false;
    setLoading(true);
    setError(false);
    dataAdapter
      .listCalendarEntries({
        sendDateFrom: rangeStart,
        sendDateTo: rangeEnd,
        pageSize: 200,
      })
      .then((res) => {
        if (!cancelled) setEntries(res.items);
      })
      .catch(() => {
        if (!cancelled) setError(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [dataAdapter, rangeStart, rangeEnd, refreshKey]);

  // Whether the daily auto-activate job is on — the move confirmations only
  // promise "CIS will activate it" when it will. Null until read.
  const [autoActivationEnabled, setAutoActivationEnabled] = useState<
    boolean | null
  >(null);
  // The weekdays we do not work (bank holidays, shutdowns), from the backend:
  // the activation date skips them as the backend's rule does (d88).
  const [closedDays, setClosedDays] = useState<string[]>([]);
  useEffect(() => {
    let cancelled = false;
    dataAdapter
      .getCampaignAutoActivation()
      .then((state) => {
        if (cancelled) return;
        setAutoActivationEnabled(state.enabled);
        setClosedDays(state.closed_days ?? []);
      })
      .catch((e) => {
        console.error("marketing.calendar.auto_activation.load.failed", e);
      });
    return () => {
      cancelled = true;
    };
  }, [dataAdapter]);

  const handleDropEntry = useCallback(
    (entryId: string, newDate: IsoDate) => {
      const entry = entries.find((e) => e.id === entryId);
      // Not actually eligible (stale drag) or dropped back on its own day.
      if (!entry || !entry.can_reschedule || entry.send_date === newDate) {
        return;
      }
      setPendingDrop({ entry, newDate });
    },
    [entries],
  );

  async function confirmPendingDrop() {
    if (!pendingDrop) return;
    const { entry, newDate } = pendingDrop;
    setPendingDrop(null);
    try {
      await dataAdapter.updateMarketingCampaignStep(
        entry.campaign_id,
        entry.id,
        { scheduled_send_date: newDate },
      );
      toast.success(
        `${entry.send_label} moved to ${formatDateOrDash(newDate)}. ${activationHint(newDate, autoActivationEnabled, "it", entry.channel, closedDays)}`,
      );
      setRefreshKey((k) => k + 1);
    } catch {
      toast.error("Couldn't move this send. Please try again.");
    }
  }

  const entriesByDate = useMemo(() => {
    const map = new Map<IsoDate, CalendarEntry[]>();
    for (const entry of entries) {
      if (!entry.send_date) continue;
      const list = map.get(entry.send_date) ?? [];
      list.push(entry);
      map.set(entry.send_date, list);
    }
    return map;
  }, [entries]);

  function goToMonth(delta: number) {
    const next = shiftMonth(year, month, delta);
    setYear(next.year);
    setMonth(next.month);
  }

  // Every chip opens a preview first rather than navigating straight away —
  // a single accidental click on a chip used to jump you off the calendar
  // with no chance to back out.
  function openEntry(entry: CalendarEntry) {
    setSelectedEntry(entry);
  }

  return (
    <DndProvider backend={HTML5Backend}>
      <div className="space-y-6">
        <MarketingNav active="calendar" />

        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl mb-1">Calendar</h1>
            <p className="text-sm text-muted-foreground">
              Every send by its real or best-known Mailchimp send date — past
              sends and what&apos;s scheduled, in one place.
            </p>
          </div>
        </div>

        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => goToMonth(-1)}
              aria-label="Previous month"
            >
              <ChevronLeft className="size-4" />
            </Button>
            <span className="text-base font-medium w-40 text-center">
              {monthLabel(year, month)}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => goToMonth(1)}
              aria-label="Next month"
            >
              <ChevronRight className="size-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setYear(Number(now.slice(0, 4)));
                setMonth(Number(now.slice(5, 7)));
              }}
            >
              Today
            </Button>
          </div>
          <div className="flex items-center gap-4 text-xs text-muted-foreground">
            <span className="flex items-center gap-1.5">
              <span className="size-2.5 rounded-full bg-muted-foreground/60" />
              Sent
            </span>
            <span className="flex items-center gap-1.5">
              <span className="size-2.5 rounded-full bg-primary" />
              Scheduled
            </span>
            <span className="flex items-center gap-1.5">
              <span className="size-2.5 rounded-full border border-dashed border-primary/50" />
              Pending
            </span>
            <span className="flex items-center gap-1.5">
              <span className="size-2.5 rounded-full bg-red-500" />
              Needs attention
            </span>
          </div>
        </div>

        {error && (
          <p className="text-sm text-destructive">
            Couldn&apos;t load the calendar. Please try again.
          </p>
        )}

        <div className="border border-border rounded-lg overflow-hidden">
          <div className="grid grid-cols-7 border-b border-border bg-surface">
            {WEEKDAY_LABELS.map((label) => (
              <div
                key={label}
                className="px-2 py-2 text-xs font-medium text-muted-foreground text-center"
              >
                {label}
              </div>
            ))}
          </div>
          <div className="grid grid-cols-7">
            {days.map((day) => (
              <DayCell
                key={day.date}
                day={day}
                entries={entriesByDate.get(day.date) ?? []}
                loading={loading}
                onOpenEntry={openEntry}
                onDropEntry={handleDropEntry}
              />
            ))}
          </div>
        </div>

        <Sheet
          open={selectedEntry !== null}
          onOpenChange={(open) => !open && setSelectedEntry(null)}
        >
          <SheetContent>
            {selectedEntry && (
              <>
                <SheetHeader>
                  <SheetTitle className="flex items-center gap-2">
                    {selectedEntry.status === "needs_attention" && (
                      <TriangleAlert className="size-4 text-red-500" />
                    )}
                    {selectedEntry.channel === "direct_mail" &&
                    selectedEntry.status !== "needs_attention"
                      ? calendarMailingState(selectedEntry.state)
                      : STATUS_LABEL[selectedEntry.status]}
                  </SheetTitle>
                  <SheetDescription>
                    {selectedEntry.send_label}
                  </SheetDescription>
                </SheetHeader>
                <div className="px-4 space-y-4">
                  <p className="text-sm text-foreground">
                    {formatDateOrDash(selectedEntry.send_date)}
                    {!selectedEntry.is_date_confirmed && " (estimated)"}
                  </p>
                  {selectedEntry.attention_reason && (
                    <p className="text-sm text-foreground">
                      {selectedEntry.attention_reason}
                    </p>
                  )}
                  <Button
                    onClick={() =>
                      navigate(
                        paths.marketingCampaignWorkspace({
                          campaignId: selectedEntry.campaign_id,
                        }),
                      )
                    }
                  >
                    Open campaign page
                  </Button>
                </div>
              </>
            )}
          </SheetContent>
        </Sheet>

        <AlertDialog
          open={pendingDrop !== null}
          onOpenChange={(open) => !open && setPendingDrop(null)}
        >
          <AlertDialogContent>
            {pendingDrop && (
              <>
                <AlertDialogHeader>
                  <AlertDialogTitle>Move this send?</AlertDialogTitle>
                  <AlertDialogDescription asChild>
                    <p>
                      Move &apos;{pendingDrop.entry.send_label}&apos; to{" "}
                      {formatDateOrDash(pendingDrop.newDate)}?{" "}
                      {activationHint(
                        pendingDrop.newDate,
                        autoActivationEnabled,
                        "it",
                        pendingDrop.entry.channel,
                        closedDays,
                      )}
                    </p>
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction onClick={confirmPendingDrop}>
                    Move
                  </AlertDialogAction>
                </AlertDialogFooter>
              </>
            )}
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </DndProvider>
  );
}
